import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { format } from "@/lib/brand-tokens";
import { members, getReservationsByMember } from "@/lib/mock-data";

// ADM-12 — Members directory
const TABS = ["all", "Active", "Suspended", "Deactivated"];

export default function AdminMembersPage() {
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => members.filter((m) => {
    if (tab !== "all" && m.status !== tab) return false;
    if (q) {
      const hay = `${m.firstName} ${m.lastName} ${m.email} ${m.id}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [tab, q]);

  const counts = useMemo(() => members.reduce((a, m) => { a[m.status] = (a[m.status] || 0) + 1; return a; }, {}), []);
  const active = members.filter((m) => m.status === "Active");
  const totalLifetime = active.reduce((a, m) => a + m.lifetimeEarnings, 0);

  return (
    <div data-testid="admin-members-page" className="space-y-5 pb-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Directory</div>
          <h1 className="font-brand text-display text-ink leading-tight mt-1">Members</h1>
          <p className="text-body-sm text-ink-muted mt-1">{members.length} total · {active.length} active · {format.currency(totalLifetime)} lifetime payouts</p>
        </div>
        <Button variant="outline"><Download className="size-4 mr-1.5" /> Export CSV</Button>
      </div>

      <div className="-mx-8 bg-surface border-y border-line">
        <div className="px-8 py-4 border-b border-line flex flex-wrap items-center gap-3">
          <div className="flex bg-surface-alt rounded-md p-0.5">
            {TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`mem-tab-${t}`}
                      className={`px-3 py-1.5 rounded-md text-caption font-semibold transition-all ${
                        tab === t ? "bg-surface text-ink shadow-xs" : "text-ink-muted hover:text-ink"
                      }`}>
                {t === "all" ? "All" : t}
                {t !== "all" && <span className="ml-1.5 text-ink-hint tabular-nums">({counts[t] || 0})</span>}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-ink-hint" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, or member ID…" className="pl-8 h-9 bg-surface-alt border-transparent focus-visible:bg-surface focus-visible:border-line" data-testid="members-search" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-body-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left">
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint">Member</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Status</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Joined</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Location</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Reservations</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Lifetime</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Last sign-in</th>
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {filtered.map((m) => {
                const reservations = getReservationsByMember(m.id).length;
                return (
                  <tr key={m.id} className="hover:bg-surface-alt/40">
                    <td className="px-8 py-3.5">
                      <Link to={`/admin/members/${m.id}`} className="flex items-center gap-3 group">
                        <div className="size-8 rounded-full bg-brand-50 text-brand-700 grid place-items-center text-caption font-semibold">{m.firstName[0]}{m.lastName[0]}</div>
                        <div className="min-w-0">
                          <div className="text-ink font-semibold group-hover:text-brand-700 transition-colors">{m.firstName} {m.lastName}</div>
                          <div className="text-caption text-ink-subtle truncate">{m.email}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-3 py-3.5"><StatusBadge entity="member" status={m.status} dot /></td>
                    <td className="px-3 py-3.5 text-ink-muted text-caption tabular-nums whitespace-nowrap">{m.memberSince ? format.date(m.memberSince) : "—"}</td>
                    <td className="px-3 py-3.5 text-ink-muted text-caption">{m.city}, {m.state}</td>
                    <td className="px-3 py-3.5 text-ink tabular-nums text-right">{reservations}</td>
                    <td className="px-3 py-3.5 text-brand-700 font-semibold tabular-nums text-right">{format.currency(m.lifetimeEarnings)}</td>
                    <td className="px-3 py-3.5 text-ink-muted text-caption tabular-nums">{m.lastSignIn ? format.relative(m.lastSignIn) : "—"}</td>
                    <td className="px-8 py-3.5 text-right">
                      <Link to={`/admin/members/${m.id}`} className="inline-flex items-center gap-1 text-caption font-semibold text-brand-700 hover:text-brand-800">
                        View <ChevronRight className="size-3.5" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
